import { WorkingDay, WorkingHoursApplyMode } from '../WorkingHours.types';
import { minutesToTime, getSlotAlphaLabel } from './slotLabelUtils';
import { SaveProductionHoursConfigurationRequest } from './productionHoursApiTypes';

// Request-side startTime/endTime are plain "HH:mm:ss" strings (see productionHoursApiTypes.ts
// for why this differs from what the backend actually returns).
const minutesToTimeString = (minutes: number) => `${minutesToTime(minutes)}:00`;

/**
 * Maps our local UI state (days/applyMode/startDate/site/system types) to the
 * POST /api/production-hours/configuration request body.
 *
 * `toDate` is confirmed to always be sent empty - the API takes it as open-ended (no
 * separate "to" date exists in our UI either, just the single "from date" picker).
 */

const applyModeToApi = (applyMode: WorkingHoursApplyMode) =>
  applyMode === 'fromDate' ? 'FromDate' : 'Today';

const todayIso = () => new Date().toISOString().slice(0, 10);

export const buildProductionHoursConfigurationPayload = ({
  siteNames,
  selectedSystemGroupTypes,
  applyMode,
  startDate,
  days,
}: {
  siteNames: string[];
  selectedSystemGroupTypes: string[];
  applyMode: WorkingHoursApplyMode;
  startDate: string;
  days: WorkingDay[];
}): SaveProductionHoursConfigurationRequest => {
  const effectiveDate = applyMode === 'fromDate' && startDate ? startDate : todayIso();
  const isoDateTime = `${effectiveDate}T00:00:00.000Z`;

  return {
    siteNames,
    systemGroupTypes: selectedSystemGroupTypes,
    applyMode: applyModeToApi(applyMode),
    fromDate: isoDateTime,
    toDate: '',
    // A disabled day is kept on to its slots locally (see DayRow.tsx) so re-enabling it
    // restores the schedule as-is - but the API rejects a non-working day sent with any
    // shifts, so a disabled day always goes out with an empty shifts array.
    days: days.map(day => ({
      dayOfWeek: day.name,
      isEnabled: day.enabled,
      shifts: day.enabled
        ? [...day.slots]
            .sort((a, b) => a.startMinutes - b.startMinutes)
            .map((slot, index) => ({
              shiftName: getSlotAlphaLabel(index),
              startTime: minutesToTimeString(slot.startMinutes),
              endTime: minutesToTimeString(slot.endMinutes),
            }))
        : [],
    })),
  };
};
